'use client';

import { useEffect, useState, useMemo } from 'react';
import { usePathname } from 'next/navigation';

// Read current query string into URLSearchParams
function readSearch() {
  if (typeof window === 'undefined') return new URLSearchParams();
  return new URLSearchParams(window.location.search);
}

export function useStaticSearchParams() {
  const [params, setParams] = useState(readSearch());
  const pathname = usePathname();

  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Patch pushState once (if not already)
    if (!window.__patchedPushStateForStaticSearch) {
      const originalPushState = window.history.pushState;
      window.history.pushState = function () {
        const result = originalPushState.apply(this, arguments);
        window.dispatchEvent(new Event('pushstate'));
        window.dispatchEvent(new Event('locationchange')); // optional alias
        return result;
      };
      window.__patchedPushStateForStaticSearch = true;
    }

    const handleUpdate = () => {
      queueMicrotask(() => {
        setParams(readSearch());
      });
    };

    window.addEventListener('popstate', handleUpdate);
    window.addEventListener('pushstate', handleUpdate);
    window.addEventListener('locationchange', handleUpdate);

    return () => {
      window.removeEventListener('popstate', handleUpdate);
      window.removeEventListener('pushstate', handleUpdate);
      window.removeEventListener('locationchange', handleUpdate);
    };
  }, []);

  useEffect(() => {
    setParams(readSearch());
  }, [pathname]);

  // Memoize to prevent unnecessary re-renders
  return useMemo(() => new URLSearchParams(params.toString()), [params]);
}
